(() => {
  if (window.__LINKED_PRODUCT_OPTIONS_ROUTE_B_V212_JSON_MATRIX__) return;
  window.__LINKED_PRODUCT_OPTIONS_ROUTE_B_V212_JSON_MATRIX__ = true;

  const IMAGE_DISPLAY_MODES = new Set([
    'square-swatch',
    'circular-swatch',
    'swatch-in-pill',
    'swatch-in-box',
    'polaroid-swatch',
  ]);

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function normalizeOptionKey(value) {
    return normalizeText(value).replace(/^options\[/, '').replace(/\]$/, '');
  }

  function normalizePath(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return String(url || '').split('?')[0].replace(/\/$/, '');
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function safeJsonParse(text) {
    if (!text || !text.trim()) return null;

    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('[Linked Product Options] Invalid linked_options_json:', error);
      return null;
    }
  }

  function clampInteger(value, fallback, min, max) {
    const number = parseInt(value, 10);
    if (Number.isNaN(number)) return fallback;
    return Math.min(max, Math.max(min, number));
  }

  function normalizeDisplayMode(value) {
    return (
      String(value || 'button-pill')
        .trim()
        .toLowerCase()
        .replace(/_/g, '-') || 'button-pill'
    );
  }

  function getRootSettings(root) {
    return {
      hideUnavailable: root.dataset.hideUnavailable !== 'false',
      hideNativeOptions: root.dataset.hideNativeOptions !== 'false',
      mobileImageRows: clampInteger(root.dataset.mobileImageRows, 3, 1, 3),
      mobileButtonRows: clampInteger(root.dataset.mobileButtonRows, 1, 1, 3),
      mobileRowsRules: parseMobileRowsRules(root.dataset.mobileRowsRules || ''),
      mobileImageCardWidth: clampInteger(root.dataset.mobileImageCardWidth, 72, 48, 140),
      showImageText: root.dataset.showImageText !== 'false',
      mobileTextLines: clampInteger(root.dataset.mobileTextLines, 2, 1, 3),
      showScrollHint: root.dataset.showScrollHint !== 'false',
      scrollHintText: root.dataset.scrollHintText || 'Swipe to see more',
    };
  }

  function parseMobileRowsRules(text) {
    const map = new Map();

    String(text || '')
      .split(/\n|;/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const parts = line.split(/=|:/);
        if (parts.length < 2) return;

        const optionName = parts[0].trim();
        const rows = clampInteger(parts.slice(1).join('=').trim(), null, 1, 3);

        if (!optionName || rows === null) return;
        map.set(normalizeOptionKey(optionName), rows);
      });

    return map;
  }

  function getRowsForOption(settings, optionName, displayMode) {
    const ruleRows = settings.mobileRowsRules.get(normalizeOptionKey(optionName));
    if (ruleRows) return ruleRows;
    return IMAGE_DISPLAY_MODES.has(displayMode) ? settings.mobileImageRows : settings.mobileButtonRows;
  }

  function normalizeOptionValues(values) {
    if (!Array.isArray(values)) return [];

    return values
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return { value: String(item), image: '' };
        }

        if (!item || typeof item !== 'object') return null;

        return {
          value: item.value ?? item.name ?? '',
          image: item.image || item.image_url || item.featured_image || '',
        };
      })
      .filter((item) => item && item.value !== '');
  }

  function normalizeOptions(rawOptions) {
    if (!Array.isArray(rawOptions)) return [];

    return rawOptions
      .map((option) => {
        if (Array.isArray(option)) {
          return {
            name: option[0] || '',
            display_mode: normalizeDisplayMode(option[1] || 'button-pill'),
            values: normalizeOptionValues(option[2]),
          };
        }

        if (!option || typeof option !== 'object') return null;

        return {
          name: option.name || option.n || '',
          display_mode: normalizeDisplayMode(option.display_mode || option.mode || option.m || 'button-pill'),
          values: normalizeOptionValues(option.values || option.v),
        };
      })
      .filter((option) => option && option.name);
  }

  function getProductUrl(product) {
    if (!product) return '';
    if (product.url) return normalizePath(product.url);
    if (product.handle) return `/products/${product.handle}`;
    return '';
  }

  function normalizeProducts(rawProducts, optionNames) {
    if (!Array.isArray(rawProducts)) return [];

    return rawProducts
      .map((product) => {
        if (Array.isArray(product)) {
          const handle = product[0] || '';
          const values = product.slice(1, optionNames.length + 1).map((value) => String(value ?? ''));
          const image = product[optionNames.length + 1] || '';
          const explicitUrl = product[optionNames.length + 2] || '';

          return {
            handle,
            url: explicitUrl ? normalizePath(explicitUrl) : handle ? `/products/${handle}` : '',
            image,
            values,
          };
        }

        if (!product || typeof product !== 'object') return null;

        let values = [];

        if (Array.isArray(product.values)) {
          values = product.values.map((value) => String(value ?? ''));
        } else if (product.values && typeof product.values === 'object') {
          values = optionNames.map((optionName) => String(product.values[optionName] ?? ''));
        }

        return {
          handle: product.handle || product.h || '',
          url: product.url ? normalizePath(product.url) : product.handle ? `/products/${product.handle}` : '',
          image: product.image || product.featured_image || product.image_url || product.i || '',
          values,
        };
      })
      .filter((product) => product && product.handle && product.values.length);
  }

  function normalizeLinkedData(rawData) {
    if (!rawData || typeof rawData !== 'object') return null;

    const options = normalizeOptions(rawData.o || rawData.options);
    const optionNames = options.map((option) => option.name);
    const products = normalizeProducts(rawData.p || rawData.products, optionNames);

    if (!options.length || !products.length) return null;

    return {
      version: rawData.v || rawData.version || 2,
      groupId: rawData.g || rawData.group_id || '',
      options,
      products,
    };
  }

  function valuesEqual(a, b) {
    const textA = normalizeText(a);
    const textB = normalizeText(b);

    if (textA === textB) return true;

    const numberA = Number(textA);
    const numberB = Number(textB);

    if (!Number.isNaN(numberA) && !Number.isNaN(numberB)) {
      return numberA === numberB;
    }

    return false;
  }

  function productValuesToObject(product, optionNames) {
    return optionNames.reduce((result, optionName, index) => {
      result[optionName] = product?.values?.[index] ?? '';
      return result;
    }, {});
  }

  function findCurrentProduct(data, currentHandle, currentUrl) {
    const normalizedCurrentUrl = normalizePath(currentUrl || window.location.pathname);

    return (
      data.products.find((product) => product.handle && product.handle === currentHandle) ||
      data.products.find((product) => getProductUrl(product) === normalizedCurrentUrl) ||
      null
    );
  }

  function findExactProduct(data, desiredValues) {
    const optionNames = data.options.map((option) => option.name);

    return (
      data.products.find((product) => {
        const productValues = productValuesToObject(product, optionNames);

        return optionNames.every((optionName) => valuesEqual(productValues[optionName], desiredValues[optionName]));
      }) || null
    );
  }

  function getUniqueValuesFromProducts(data, optionName) {
    const optionNames = data.options.map((option) => option.name);
    const seen = new Set();
    const values = [];

    data.products.forEach((product) => {
      const productValues = productValuesToObject(product, optionNames);
      const value = productValues[optionName];
      const key = normalizeText(value);

      if (!key || seen.has(key)) return;

      seen.add(key);
      values.push({ value, image: '' });
    });

    return values;
  }

  function getOptionValues(data, option) {
    if (Array.isArray(option.values) && option.values.length) {
      const productsValues = getUniqueValuesFromProducts(data, option.name);
      const productValueKeys = new Set(productsValues.map((item) => normalizeText(item.value)));

      return option.values.filter((item) => productValueKeys.has(normalizeText(item.value)));
    }

    return getUniqueValuesFromProducts(data, option.name);
  }

  function isImageDisplayMode(displayMode) {
    return IMAGE_DISPLAY_MODES.has(displayMode);
  }

  function getNativeOptionName(wrapper) {
    const label = wrapper.querySelector('.form__label, legend, label');
    const select = wrapper.querySelector('select[name^="options["]');
    const checkedInput = wrapper.querySelector('input[name^="options["]:checked');
    const firstInput = wrapper.querySelector('input[name^="options["]');

    if (label) {
      const labelClone = label.cloneNode(true);
      labelClone.querySelectorAll('[data-selected-value], [data-linked-current-value], span').forEach((node) => node.remove());
      const fromLabel = (labelClone.textContent || '').split(':')[0].trim();
      if (fromLabel) return fromLabel;
    }

    const name = select?.name || checkedInput?.name || firstInput?.name || '';
    return name.replace(/^options\[/, '').replace(/\]$/, '').trim();
  }

  function hideNativeVariantOptions(root, data) {
    const settings = getRootSettings(root);
    if (!settings.hideNativeOptions) return;

    const productInfo = root.closest('product-info');
    const variantSelects = productInfo?.querySelector('variant-selects');
    if (!variantSelects) return;

    const linkedKeys = new Set(data.options.map((option) => normalizeOptionKey(option.name)));

    variantSelects.querySelectorAll('.product-form__input').forEach((wrapper) => {
      if (wrapper.closest('linked-product-options')) return;

      const optionName = getNativeOptionName(wrapper);
      if (!optionName) return;

      if (linkedKeys.has(normalizeOptionKey(optionName))) {
        wrapper.hidden = true;
        wrapper.classList.add('product-form__input--linked-hidden');
        wrapper.dataset.linkedHiddenOption = optionName;
      }
    });
  }

  function buildChoiceButton({ optionName, optionValue, displayMode, currentValue, targetProduct, targetProductValues, valueItem, settings }) {
    const targetUrl = getProductUrl(targetProduct);
    const isActive = valuesEqual(currentValue, optionValue);
    const isAvailable = Boolean(targetProduct && targetUrl);
    const image = targetProduct?.image || valueItem.image || '';
    const showMedia = isImageDisplayMode(displayMode);

    const media = showMedia
      ? image
        ? `<span class="linked-product-options__media"><img src="${escapeHtml(image)}" alt="${escapeHtml(optionValue)}" loading="eager"></span>`
        : '<span class="linked-product-options__media"><span class="linked-product-options__placeholder" aria-hidden="true"></span></span>'
      : '';

    return `
      <button
        type="button"
        class="linked-product-options__item${isActive ? ' is-active' : ''}${!isAvailable ? ' is-unavailable' : ''}"
        data-linked-product-choice
        data-linked-option-name="${escapeHtml(optionName)}"
        data-linked-option-value="${escapeHtml(optionValue)}"
        data-product-url="${escapeHtml(targetUrl)}"
        data-linked-product-url="${escapeHtml(targetUrl)}"
        data-product-handle="${escapeHtml(targetProduct?.handle || '')}"
        aria-label="${escapeHtml(optionName)}: ${escapeHtml(optionValue)}${isAvailable ? '' : ' unavailable'}"
        aria-current="${isActive ? 'true' : 'false'}"
        aria-disabled="${isAvailable ? 'false' : 'true'}"
        ${!isAvailable ? 'disabled' : ''}
      >
        ${media}
        <span class="linked-product-options__text">${escapeHtml(optionValue)}</span>
      </button>
    `;
  }

  function renderLinkedOptions(root) {
    const jsonScript = root.querySelector('script[data-linked-options-json]');
    const rawData = safeJsonParse(jsonScript?.textContent || '');
    const data = normalizeLinkedData(rawData);

    if (!data) {
      root.hidden = true;
      return;
    }

    const settings = getRootSettings(root);
    const optionNames = data.options.map((option) => option.name);
    const currentProduct = findCurrentProduct(data, root.dataset.currentProductHandle, root.dataset.currentProductUrl);

    if (!currentProduct) {
      console.warn('[Linked Product Options] Current product was not found in linked_options_json.');
      root.hidden = true;
      return;
    }

    hideNativeVariantOptions(root, data);

    const currentValues = productValuesToObject(currentProduct, optionNames);
    const fieldsets = [];

    root.classList.toggle('linked-product-options--hide-image-text', !settings.showImageText);
    root.style.setProperty('--linked-mobile-image-card-width', `${settings.mobileImageCardWidth}px`);
    root.style.setProperty('--linked-mobile-text-lines', String(settings.mobileTextLines));

    data.options.forEach((option) => {
      if (!option?.name) return;

      const optionName = option.name;
      const currentValue = currentValues[optionName] ?? '';
      const displayMode = normalizeDisplayMode(option.display_mode);
      const optionValues = getOptionValues(data, option);
      const rows = getRowsForOption(settings, optionName, displayMode);
      const isImageMode = isImageDisplayMode(displayMode);

      const items = optionValues
        .map((valueItem) => {
          const optionValue = valueItem.value;
          const desiredValues = { ...currentValues, [optionName]: optionValue };
          const targetProduct = findExactProduct(data, desiredValues);

          if (settings.hideUnavailable && !targetProduct) {
            return '';
          }

          const targetProductValues = targetProduct ? productValuesToObject(targetProduct, optionNames) : desiredValues;

          return buildChoiceButton({
            optionName,
            optionValue,
            displayMode,
            currentValue,
            targetProduct,
            targetProductValues,
            valueItem,
            settings,
          });
        })
        .join('');

      if (!items.trim()) return;

      fieldsets.push(`
        <fieldset class="js product-form__input product-form__input--linked-product linked-product-options__fieldset linked-product-options__fieldset--${escapeHtml(displayMode)}" data-linked-option-name="${escapeHtml(optionName)}">
          <legend class="form__label linked-product-options__legend">
            <span class="linked-product-options__label-text">
              ${escapeHtml(optionName)}:
              <span data-linked-current-value>${escapeHtml(currentValue)}</span>
            </span>
            ${settings.showScrollHint && settings.scrollHintText ? `<span class="linked-product-options__scroll-hint" aria-hidden="true">${escapeHtml(settings.scrollHintText)}</span>` : ''}
          </legend>
          <div class="linked-product-options__scroll-frame" data-linked-scroll-frame>
            <div
              class="linked-product-options__list linked-product-options__list--mobile-scroll ${isImageMode ? 'linked-product-options__list--image-mode' : 'linked-product-options__list--button-mode'}"
              role="list"
              style="--linked-mobile-rows: ${rows}; --linked-mobile-image-card-width: ${settings.mobileImageCardWidth}px; --linked-mobile-text-lines: ${settings.mobileTextLines};"
            >
              ${items}
            </div>
          </div>
        </fieldset>
      `);
    });

    root.classList.add('linked-product-options--ready');
    root.innerHTML = fieldsets.join('');
    setupScrollHints(root);
  }


  function updateScrollFrameState(frame) {
    const list = frame.querySelector('.linked-product-options__list--mobile-scroll');
    if (!list) return;

    const fieldset = frame.closest('.linked-product-options__fieldset');
    const isScrollable = list.scrollWidth > list.clientWidth + 2;
    const atStart = list.scrollLeft <= 2;
    const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
    const hasScrolled = list.dataset.linkedUserScrolled === 'true';

    frame.classList.toggle('is-scrollable', isScrollable);
    frame.classList.toggle('is-at-start', !isScrollable || atStart);
    frame.classList.toggle('is-at-end', !isScrollable || atEnd);
    frame.classList.toggle('has-scrolled', hasScrolled);

    fieldset?.classList.toggle('is-scrollable', isScrollable);
    fieldset?.classList.toggle('is-at-start', !isScrollable || atStart);
    fieldset?.classList.toggle('is-at-end', !isScrollable || atEnd);
    fieldset?.classList.toggle('has-scrolled', hasScrolled);
  }

  function setupScrollHints(root) {
    const settings = getRootSettings(root);
    root.classList.toggle('linked-product-options--scroll-hint-enabled', settings.showScrollHint);

    root.querySelectorAll('[data-linked-scroll-frame]').forEach((frame) => {
      const list = frame.querySelector('.linked-product-options__list--mobile-scroll');
      if (!list) return;

      if (frame.dataset.linkedScrollHintBound !== 'true') {
        frame.dataset.linkedScrollHintBound = 'true';

        list.addEventListener(
          'scroll',
          () => {
            if (Math.abs(list.scrollLeft) > 2) {
              list.dataset.linkedUserScrolled = 'true';
            }

            updateScrollFrameState(frame);
          },
          { passive: true }
        );

        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(() => updateScrollFrameState(frame));
          observer.observe(list);
          observer.observe(frame);
        } else {
          window.addEventListener('resize', () => updateScrollFrameState(frame));
        }
      }

      requestAnimationFrame(() => updateScrollFrameState(frame));
      window.setTimeout(() => updateScrollFrameState(frame), 350);
    });
  }

  function setLoading(choice, isLoading) {
    const productInfo = choice.closest('product-info');
    choice.classList.toggle('is-loading', Boolean(isLoading));
    productInfo?.classList.toggle('linked-product-options--loading', Boolean(isLoading));
  }

  function handleChoiceClick(event) {
    const choice = event.target.closest('[data-linked-product-choice]');
    if (!choice) return;

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (choice.disabled || choice.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    const productUrl = choice.dataset.productUrl || choice.dataset.linkedProductUrl || choice.getAttribute('href');
    if (!productUrl || productUrl === '#') return;

    const targetPath = normalizePath(productUrl);
    if (!targetPath) return;

    const currentPath = normalizePath(window.location.pathname);
    if (targetPath === currentPath) {
      event.preventDefault();
      return;
    }

    const productInfo = choice.closest('product-info');

    if (!productInfo || typeof productInfo.handleOptionValueChange !== 'function') {
      if (choice.tagName === 'A') return;
      window.location.href = targetPath;
      return;
    }

    event.preventDefault();

    if (!choice.id) {
      choice.id = `LinkedProductOption-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    }

    choice.dataset.productUrl = targetPath;
    setLoading(choice, true);

    productInfo.handleOptionValueChange({
      data: {
        event,
        target: choice,
        selectedOptionValues: [],
      },
    });

    window.setTimeout(() => {
      setLoading(choice, false);
    }, 1800);
  }

  class LinkedProductOptions extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      const jsonScript = this.querySelector('script[data-linked-options-json]');
      if (jsonScript) renderLinkedOptions(this);
      setupScrollHints(this);
    }
  }

  if (!customElements.get('linked-product-options')) {
    customElements.define('linked-product-options', LinkedProductOptions);
  } else {
    document.querySelectorAll('linked-product-options').forEach((root) => {
      const jsonScript = root.querySelector('script[data-linked-options-json]');
      if (jsonScript && root.dataset.initialized !== 'true') renderLinkedOptions(root);
      setupScrollHints(root);
    });
  }

  document.addEventListener('click', handleChoiceClick);
})();
